"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import {
  Loader2,
  TriangleAlert,
  RotateCcw,
  Eye,
  ArrowRight,
  HardDriveDownload,
  Phone,
  Cloud,
  Wrench,
  BarChart3,
  Shield,
  Database,
  Smartphone,
  Monitor,
  Server,
  Network,
  Code,
  type LucideIcon,
} from "lucide-react";
import productService, { Product } from "../../../services/productService";
import styles from "./page.module.css";
import { buildProductMediaUrl } from "@/utils/productMediaUrl";

// Icon mapping giống Vue component
const iconMap: { [key: string]: LucideIcon } = {
  Cloud,
  Wrench,
  BarChart3,
  Shield,
  Database,
  Smartphone,
  Monitor,
  Server,
  Network,
  Code,
  // Fallback map từ FontAwesome sang Lucide
  "fas fa-cloud": Cloud,
  "fas fa-tools": Wrench,
  "fas fa-chart-bar": BarChart3,
  "fas fa-shield-alt": Shield,
  "fas fa-database": Database,
  "fas fa-mobile-alt": Smartphone,
  "fas fa-desktop": Monitor,
  "fas fa-server": Server,
  "fas fa-network-wired": Network,
  "fas fa-code": Code,
};

const getIconComponent = (iconString?: string): LucideIcon => {
  if (!iconString) return Cloud;
  if (typeof iconString === "object" && iconString) {
    return iconString as LucideIcon;
  }
  return iconMap[iconString] || Cloud;
};

// Helper function để tạo slug từ title
const createSlug = (title: string): string => {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Loại bỏ dấu
    .replace(/[đĐ]/g, "d")
    .replace(/[^a-z0-9\s-]/g, "") // Loại bỏ ký tự đặc biệt
    .trim()
    .replace(/\s+/g, "-") // Thay khoảng trắng bằng dấu gạch ngang
    .replace(/-+/g, "-") // Loại bỏ nhiều dấu gạch ngang liên tiếp
    .replace(/^-+|-+$/g, ""); // Loại bỏ dấu gạch ngang ở đầu và cuối
};

export default function SanPhamPage() {
  const router = useRouter();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await productService.getProducts({
        status: "active",
        limit: 12,
      });

      if (response && response.data && response.data.products) {
        // Xử lý URL cho file ZIP trong danh sách sản phẩm
        setProducts(
          response.data.products.map((product) => ({
            ...product,
            zipFile: product.zipFile
              ? buildProductMediaUrl(product.zipFile)
              : undefined,
            dashboardImage: product.dashboardImage
              ? buildProductMediaUrl(product.dashboardImage)
              : "/assets/sdtc-image/trang-chu/Logo/sdtc.webp",
          }))
        );
      } else {
        setProducts([]);
      }
    } catch (error) {
      setError("Không thể tải danh sách sản phẩm");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = (product: Product) => {
    const slug = createSlug(product.title);
    router.push(`/san-pham/${slug}` as any);
  };

  const downloadProduct = async (product: Product) => {
    try {
      // Kiểm tra xem sản phẩm có file ZIP không
      if (!product.zipFile) {
        showToast("File sản phẩm không khả dụng", "error");
        return;
      }

      showToast("Đang tải xuống sản phẩm...", "info");

      const getOriginalFileName = (url: string): string => {
        try {
          const fileName = url.split("/").pop() || "";
          return fileName.split("?")[0];
        } catch {
          return `${product.title.replace(/[^a-z0-9]/gi, "_")}.zip`;
        }
      };

      const originalFileName = getOriginalFileName(product.zipFile);

      try {
        const response = await fetch(product.zipFile, {
          method: "GET",
          headers: {
            Accept: "application/zip, application/octet-stream, */*",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = originalFileName;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        showToast("Tải xuống sản phẩm thành công!", "success");
      } catch (fetchError) {
        console.warn("Fetch failed, using direct link:", fetchError);

        const link = document.createElement("a");
        link.href = product.zipFile;
        link.download = originalFileName;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
        }, 100);

        showToast("Đang tải xuống sản phẩm...", "info");
      }
    } catch (error) {
      console.error("Lỗi khi tải xuống sản phẩm:", error);
      showToast("Không thể tải xuống sản phẩm", "error");
    }
  };

  const contactUs = () => {
    router.push("/dat-lich");
  };

  const showToast = (message: string, type: "success" | "error" | "warning" | "info" = "info") => {
    try {
      toast[type](message, {
        position: "top-right",
        autoClose: type === "error" ? 4000 : 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      // Toast không khả dụng, bỏ qua
    }
  };

  return (
    <section className={styles.productsSection}>
      <div className={styles.container}>
        {/* Header Section */}
        <div className={styles.productsHeader}>
          <h2 className={styles.productsTitle}>Sản Phẩm Công Nghệ</h2>
          <p className={styles.productsSubtitle}>
            Khám phá bộ giải pháp công nghệ hiện đại toàn diện của chúng tôi được
            thiết kế để tối ưu hóa quy trình làm việc và tăng năng suất.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}>
              <Loader2 className={`${styles.customIcon} ${styles.spinning}`} />
              <p>Đang tải sản phẩm...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className={styles.errorContainer}>
            <div className={styles.errorMessage}>
              <TriangleAlert className={styles.customIcon} />
              <p>{error}</p>
              <button onClick={loadProducts} className={styles.btnRetry}>
                <RotateCcw className={styles.customIcon} />
                Thử lại
              </button>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {!loading && !error && (
          <div className={styles.productsGrid}>
            {products.map((product) => {
              const authorName =
                product.productCreator?.fullName ||
                (product.createdBy && typeof product.createdBy === "object"
                  ? product.createdBy.fullName
                  : undefined) ||
                "Đội ngũ SDTC";
              const IconComponent = getIconComponent(product.icon);
              return (
                <div key={product.id} className={styles.productCard}>
                  {/* Product Icon */}
                  <div className={styles.productIcon}>
                    <IconComponent
                      className={styles.customIcon}
                      style={{ color: product.iconColor || "#265ff3" }}
                    />
                  </div>

                  {/* Category Tag */}
                  <div className={styles.productCategory}>
                    {product.category}
                  </div>

                  {/* Product Title */}
                  <h3 className={styles.productTitle}>{product.title}</h3>

                  {/* Product Description */}
                  <p className={styles.productDescription}>
                    {product.description}
                  </p>

                  {/* Action Buttons */}
                  <div className={styles.productActions}>
                    <button
                      className={styles.btnViewDetails}
                      onClick={() => viewDetails(product)}
                    >
                      <Eye className={styles.customIcon} />
                      <span>Xem Chi Tiết</span>
                      <ArrowRight className={styles.customIcon} />
                    </button>
                    {product.zipFile ? (
                      <button
                        className={styles.btnDownload}
                        onClick={() => downloadProduct(product)}
                      >
                        <HardDriveDownload className={styles.customIcon} />
                        <span>Tải Xuống</span>
                      </button>
                    ) : (
                      <button
                        className={styles.btnContact}
                        onClick={contactUs}
                      >
                        <Phone className={styles.customIcon} />
                        <span>Liên Hệ Tư Vấn</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}